import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {getTableSkeletonHeight} from 'notebook/tables/core-ui/TableSkeleton';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import areAllArchivableOutputFilesArchived from 'runs/areAllArchivableOutputFilesArchived';
import {NLADVersionedData, NoteLinkedAssayData} from 'runs/AttachmentConstants';
import LegacyNoteLinkedAssayResultsFrame from 'runs/LegacyNoteLinkedAssayResultsFrame';
import NoteLinkedAssayDataComponent from 'runs/NoteLinkedAssayData';
import * as NoteLinkedAssayDataUtil from 'runs/NoteLinkedAssayDataUtil';

export const NOTE_LINKED_ASSAY_DATA_BASE_HEIGHT = 409;

export const AUTOMATION_FILE_HEIGHT = 71;

const NOTE_LINKED_ASSAY_DATA_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.NOTE_LINKED_ASSAY_DATA,
  title: MODEL_NAMES.Run,
  icon: 'note-table',
  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],
    getVirtualizedHeight(item: NotePart & {item: NoteLinkedAssayData}) {
      // TODO(BNCH-69538): cache the computed virtualized height
      let height = NOTE_LINKED_ASSAY_DATA_BASE_HEIGHT;

      const noteLinkedAssayData = item.item;
      noteLinkedAssayData.dataTables.forEach((dataTable) => {
        height += getTableSkeletonHeight(dataTable.rows.length, false);
      });

      const versionedData = noteLinkedAssayData.versionedData;
      versionedData.automationOutputFiles?.forEach((output) => {
        if (output.blob != null) {
          height += AUTOMATION_FILE_HEIGHT;
        }
      });
      versionedData.automationInputFiles?.forEach((output) => {
        if (output.blob != null) {
          height += AUTOMATION_FILE_HEIGHT;
        }
      });
      versionedData.automationErrorFiles?.forEach((output) => {
        if (output.blob != null) {
          height += AUTOMATION_FILE_HEIGHT;
        }
      });
      versionedData.structuredTables.forEach((table) => {
        height += getTableSkeletonHeight(table.rows.length, false);
      });
      return height;
    },
    make(
      this: NoteEditor,
      item: NotePart & {item: NoteLinkedAssayData},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: {}
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const noteLinkedAssayData: NoteLinkedAssayData = item.item;

      function updateVersionedData(updates: Partial<NLADVersionedData>): NLADVersionedData {
        return {
          ...noteLinkedAssayData.versionedData,
          ...updates,
        };
      }

      const assayResultIds = noteLinkedAssayData.assayResultIds;
      const assayRunId = noteLinkedAssayData.assayRunId;
      const assayRunSchema = noteLinkedAssayData.assayRunSchema;
      assert(
        (assayResultIds.length !== 0) !== (assayRunId != null || assayRunSchema != null),
        'Exactly one of assayResultIds and assayRun details should be set',
        {noteLinkedAssayData}
      );
      if (assayRunSchema != null) {
        return (
          <NoteLinkedAssayDataComponent
            assayRunSchemaInfo={{
              assayRunSchema,
              versionedName: noteLinkedAssayData.versionedData.runSchemaName,
              versionedFields: noteLinkedAssayData.versionedData.fields,
            }}
            assayRunInfo={{
              assayRunId,
              versionedTags: noteLinkedAssayData.versionedData.tags,
              versionedConnectionId: noteLinkedAssayData.versionedData.connectionId,
              versionedEquipmentId: noteLinkedAssayData.versionedData.equipmentId,
            }}
            assayResultSchemas={noteLinkedAssayData.assayResultSchemas}
            automationFiles={{
              automationInputFiles: noteLinkedAssayData.versionedData.automationInputFiles,
              automationOutputFiles: noteLinkedAssayData.versionedData.automationOutputFiles,
              automationErrorFiles: noteLinkedAssayData.versionedData.automationErrorFiles,
            }}
            dataTables={noteLinkedAssayData.dataTables}
            displayConfigBySchemaId={noteLinkedAssayData.displayConfigBySchemaId}
            provenanceUuid={noteLinkedAssayData.provenanceUuid}
            cid={cid}
            fileId={editorProps.fileId}
            indentation={this.getIndentation(item)}
            index={index}
            locked={editorProps.locked}
            versionedStructuredTables={noteLinkedAssayData.versionedData.structuredTables}
            viewer={editorProps.viewer}
            registerObjects={editorProps.registerObjects}
            storeState={editorProps.storeState}
            updateHandler={this.itemUpdateHandler}
            updateVersionedData={updateVersionedData}
            usedTableNames={editorProps.usedTableNames}
            editor={this}
            editorProps={editorProps}
          />
        );
      }
      return (
        <LegacyNoteLinkedAssayResultsFrame
          assayResultIds={assayResultIds}
          assayResultSchemas={noteLinkedAssayData.assayResultSchemas}
          displayConfigBySchemaId={noteLinkedAssayData.displayConfigBySchemaId}
          cid={cid}
          fileId={editorProps.fileId}
          indentation={this.getIndentation(item)}
          index={index}
          locked={editorProps.locked}
          viewer={editorProps.viewer}
          registerObjects={editorProps.registerObjects}
          storeState={editorProps.storeState}
          updateHandler={this.itemUpdateHandler}
          editor={this}
          editorProps={editorProps}
        />
      );
    },
  },
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: NoteLinkedAssayData}): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },
  itemFromHTML(
    this: NoteEditor,
    $el: JQuery,
    notePart: NotePart & {item: NoteLinkedAssayData}
  ): NotePart | null {
    if (notePart.item.assayRunId == null) {
      return notePart;
    }
    if (
      $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) !== this.props.fileId ||
      NoteLinkedAssayDataUtil.getNoteLinkedAssayDatasInNote(this.props.note)
        .map((nlad: NoteLinkedAssayData) => nlad.assayRunId)
        .includes(notePart.item.assayRunId)
    ) {
      const newNlad: NoteLinkedAssayData = {
        ...notePart.item,
        assayResultIds: [],
        assayResultSchemas: [],
        assayRunId: null,
        dataTables: [],
        versionedData: {
          ...notePart.item.versionedData,
          // Remove all versioned data that shouldn't be copied to a new Entry or Template but don't remove
          // tags, connectionId or equipmentId as we want to be able to copy/paste those to new Runs
          structuredTables: [],
          automationErrorFiles: [],
          automationInputFiles: [],
          automationOutputFiles: [],
        },
      };
      return {
        ...notePart,
        item: newNlad,
      };
    }
    return notePart;
  },

  getContent({item}: NotePart & {item: NoteLinkedAssayData}): {item: NoteLinkedAssayData} {
    return {item};
  },
  setContent(props: {item: NoteLinkedAssayData} | Partial<NoteLinkedAssayData>): Spec<NotePart> {
    // eslint-disable-next-line benchlint/no-in-operator
    if ('item' in props && props.item != null) {
      return {$merge: {item: props.item}};
    } else {
      return {item: {$merge: props as Partial<NoteLinkedAssayData>}};
    }
  },
  getReasonForDisallowedAction(
    this: NoteEditor,
    item: NotePart & {item: NoteLinkedAssayData},
    action: MediocreItemAction
  ): string | null {
    const automationOutputFiles = item.item.versionedData.automationOutputFiles ?? [];
    switch (action) {
      case MediocreItemAction.DELETE: {
        if (item.item.assayRunId != null && !areAllArchivableOutputFilesArchived(automationOutputFiles)) {
          return 'Please archive all Results before deleting the Run';
        }
        return null;
      }
      default:
        return null;
    }
  },
};
export default NOTE_LINKED_ASSAY_DATA_PLUGIN;
